import { format, addDays } from "date-fns";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";
import { cn } from "@/lib/utils";

import { CALENDAR_SOURCES, findChoice, PRIORITIES } from "../common/constants";
import {
  isOverdue,
  ProgressBar,
  ProgressRing,
  SectionCard,
  StatusBadge,
  useFormatDate,
} from "../common/components";
import {
  deliveryHealth,
  HEALTH_COLORS,
  useDeliveries,
} from "../delivery/useDelivery";
import type { CalendarItem, TeamTask } from "../types";

/**
 * Fretum hub widgets shown on the «Hoy» home (below the fold): my team
 * tasks, deliveries in progress and the next 7 days of the calendar.
 */
export const MyTasks = () => {
  const translate = useTranslate();
  const formatDate = useFormatDate();
  const { identity } = useGetIdentity();
  const { data: tasks } = useGetList<TeamTask>(
    "team_tasks",
    {
      filter: { assignee_id: identity?.id, "status@neq": "done" },
      sort: { field: "due_date", order: "ASC" },
      pagination: { page: 1, perPage: 6 },
    },
    { enabled: identity?.id != null },
  );
  return (
    <SectionCard
      title={
        <Link to="/team_tasks" className="hover:underline">
          {translate("hub.dashboard.my_tasks")}
        </Link>
      }
    >
      {!tasks?.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("hub.dashboard.no_tasks")}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {tasks.map((task) => (
            <li key={task.id} className="flex items-center gap-2 text-sm">
              <Link
                to={`/team_tasks/${task.id}`}
                className="flex-1 truncate hover:underline"
              >
                {task.title}
              </Link>
              {task.priority !== "medium" && (
                <StatusBadge choice={findChoice(PRIORITIES, task.priority)} />
              )}
              <span
                className={cn(
                  "shrink-0 text-xs text-muted-foreground",
                  isOverdue(task.due_date) && "font-medium text-destructive",
                )}
              >
                {task.due_date
                  ? formatDate(task.due_date, {
                      day: "numeric",
                      month: "short",
                    })
                  : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
};

export const Deliveries = () => {
  const translate = useTranslate();
  const { data: deliveries } = useDeliveries();
  const active = (deliveries ?? []).filter((d) => d.progress < 100);
  const { data: deals } = useGetList(
    "deals",
    {
      filter: { "id@in": `(${active.map((d) => d.id).join(",")})` },
      pagination: { page: 1, perPage: 50 },
    },
    { enabled: active.length > 0 },
  );
  return (
    <SectionCard
      title={
        <Link to="/deals" className="hover:underline">
          {translate("hub.dashboard.deliveries")}
        </Link>
      }
    >
      {!active.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("hub.dashboard.no_deliveries")}
        </p>
      ) : (
        <ul className="space-y-2">
          {active.map((delivery) => {
            const deal = deals?.find(
              (d) => String(d.id) === String(delivery.id),
            );
            const health = deliveryHealth(delivery);
            return (
              <li key={delivery.id} className="flex items-center gap-2 text-sm">
                <ProgressRing value={delivery.progress} />
                <div className="min-w-0 flex-1 space-y-1">
                  <Link
                    to={`/deals/${delivery.id}/show`}
                    className="flex items-center gap-2 hover:underline"
                  >
                    <span
                      className="size-2 shrink-0 rounded-full"
                      style={{ backgroundColor: HEALTH_COLORS[health] }}
                      title={translate(`hub.delivery.${health}`)}
                    />
                    <span className="truncate">{deal?.name ?? "…"}</span>
                  </Link>
                  <ProgressBar value={delivery.progress} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
};

/** The SQL view paginates; the in-memory demo view does not. */
const NEXT_DAYS_MAX = 8;

export const NextDays = () => {
  const translate = useTranslate();
  const formatDate = useFormatDate();
  const today = format(new Date(), "yyyy-MM-dd");
  const { data: items } = useGetList<CalendarItem>("calendar_items", {
    filter: {
      "start_at@gte": today,
      "start_at@lte": format(addDays(new Date(), 7), "yyyy-MM-dd"),
      "status@neq": "done",
    },
    sort: { field: "start_at", order: "ASC" },
    pagination: { page: 1, perPage: 8 },
  });
  return (
    <SectionCard
      title={
        <Link to="/calendar" className="hover:underline">
          {translate("hub.dashboard.next_days")}
        </Link>
      }
    >
      {!items?.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("hub.calendar.no_items")}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, NEXT_DAYS_MAX).map((item) => (
            <li key={item.id} className="flex items-center gap-2 text-sm">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: CALENDAR_SOURCES[item.source].color }}
                title={translate(`hub.calendar.sources.${item.source}`)}
              />
              <span className="flex-1 truncate">{item.title}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatDate(item.start_at.slice(0, 10), {
                  weekday: "short",
                  day: "numeric",
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
};
